import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Stack,
  Typography,
  Checkbox,
  Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';

const FORMATS = [
  {
    value: 'txt',
    icon: DescriptionOutlinedIcon,
    label: 'Textdatei (.txt)',
    hint: 'Einfacher Fließtext mit Zeitstempeln – gut für Notizen und Protokolle.',
  },
  {
    value: 'srt',
    icon: SubtitlesOutlinedIcon,
    label: 'Untertitel (.srt)',
    hint: 'Untertitel-Format für Video-Player, YouTube, Premiere, DaVinci etc.',
  },
];

export default function DownloadDialog({
  open,
  onClose,
  onConfirm,
  hasSpeakers,
  defaultGrouped,
}) {
  const [format, setFormat] = useState('txt');
  const [withSpeaker, setWithSpeaker] = useState(true);
  const [groupBySpeaker, setGroupBySpeaker] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormat('txt');
    setWithSpeaker(hasSpeakers);
    // Vorbelegung aus der aktuellen Ansicht: wer kompakt liest, laedt
    // standardmaessig auch kompakt herunter — kann es hier aber abwaehlen.
    setGroupBySpeaker(hasSpeakers && Boolean(defaultGrouped));
  }, [open, hasSpeakers, defaultGrouped]);

  // Gruppierung gibt es nur im TXT. Ein SRT-Cue ueber mehrere Minuten mit einem
  // ganzen Absatz Text waere als Untertitel unbrauchbar.
  const groupingAvailable = hasSpeakers && format === 'txt';

  const handleConfirm = () => {
    onConfirm?.({
      format,
      withSpeaker: hasSpeakers ? withSpeaker : false,
      groupBySpeaker: groupingAvailable && groupBySpeaker,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>Transkription herunterladen</DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.85rem' }}>
            Format
          </FormLabel>
          <RadioGroup
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            <Stack spacing={1}>
              {FORMATS.map((f) => {
                const Icon = f.icon;
                const selected = format === f.value;
                return (
                  <Box
                    key={f.value}
                    onClick={() => setFormat(f.value)}
                    sx={{
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      transition: 'border-color 120ms ease',
                      bgcolor: selected ? 'action.selected' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value={f.value}
                      control={<Radio size="small" />}
                      sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ pt: 0.25 }}
                        >
                          <Icon
                            fontSize="small"
                            sx={{ color: 'text.secondary', mt: '2px' }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {f.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {f.hint}
                            </Typography>
                          </Box>
                        </Stack>
                      }
                    />
                  </Box>
                );
              })}
            </Stack>
          </RadioGroup>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hasSpeakers && withSpeaker}
                onChange={(e) => setWithSpeaker(e.target.checked)}
                disabled={!hasSpeakers}
              />
            }
            label={
              <Typography variant="body2">
                Sprechernamen einschließen
              </Typography>
            }
          />
          {!hasSpeakers && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', ml: 4, mt: -0.5 }}
            >
              In dieser Transkription wurden keine Sprecher erkannt.
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={groupingAvailable && groupBySpeaker}
                onChange={(e) => setGroupBySpeaker(e.target.checked)}
                disabled={!groupingAvailable}
              />
            }
            label={
              <Typography variant="body2">
                Beiträge pro Sprecher zusammenfassen
              </Typography>
            }
          />
          {hasSpeakers && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', ml: 4, mt: -0.5 }}
            >
              {format === 'srt'
                ? 'Für Untertitel nicht möglich – Cues müssen kurz bleiben.'
                : 'Aufeinanderfolgende Segmente desselben Sprechers werden zu einem Absatz zusammengefasst – wie in der kompakten Ansicht.'}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleConfirm}
        >
          Herunterladen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
